import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  FileText,
  DollarSign,
  Calendar,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowRight,
  Loader2,
  User,
  LogOut,
  Upload,
  ChevronDown,
  ChevronUp,
  Building2,
  Send,
  ExternalLink,
  Crown,
  Users,
  TrendingUp,
  CreditCard,
  Target,
  Home,
  X,
  Lock,
  Settings
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import DocumentUpload from "../components/DocumentUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: "Pending Review", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  reviewed: { label: "Reviewed", color: "bg-blue-100 text-blue-700", icon: FileText },
  contacted: { label: "Contacted", color: "bg-purple-100 text-purple-700", icon: User },
  in_progress: { label: "In Progress", color: "bg-cyan-100 text-cyan-700", icon: Clock },
  sent_to_bank: { label: "Sent to Bank", color: "bg-indigo-100 text-indigo-700", icon: Send },
  approved: { label: "Approved", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  declined: { label: "Declined", color: "bg-red-100 text-red-700", icon: XCircle },
  completed: { label: "Completed", color: "bg-gray-100 text-gray-700", icon: CheckCircle2 },
};

export default function DashboardPage() {
  const { user, token, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDocuments, setShowDocuments] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [expandedApp, setExpandedApp] = useState(null);
  const [connectingBank, setConnectingBank] = useState(null);
  const [showFinancialPlannerModal, setShowFinancialPlannerModal] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [requestingPlanner, setRequestingPlanner] = useState(false);
  
  // Change password state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const financialPlannerServices = [
    { id: "tdsr_improvement", icon: TrendingUp, title: "TDSR Improvement", desc: "Get a personalized plan to lower your debt ratio" },
    { id: "debt_reduction", icon: CreditCard, title: "Debt Reduction Strategy", desc: "Principal payments & credit card management advice" },
    { id: "early_repayment", icon: Target, title: "Early Repayment Roadmap", desc: "Plan to pay off your loans faster" },
    { id: "home_buying", icon: Home, title: "Home Buying Guide", desc: "Requirements & steps to homeownership" },
    { id: "comprehensive_report", icon: FileText, title: "Comprehensive Financial Report", desc: "Full analysis of your financial health" },
  ];

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    fetchApplications();
  }, [token, navigate]);

  const fetchApplications = async () => {
    try {
      const res = await axios.get(`${API}/user/applications`, {
        headers: getAuthHeader()
      });
      setApplications(res.data);
    } catch (error) {
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    if (documents.length > 0) return; // Already loaded
    setLoadingDocs(true);
    try {
      const res = await axios.get(`${API}/documents/user`, {
        headers: getAuthHeader()
      });
      setDocuments(res.data);
    } catch (error) {
      console.error("Failed to load documents:", error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleToggleDocuments = () => {
    if (!showDocuments) {
      fetchDocuments();
    }
    setShowDocuments(!showDocuments);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'BSD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, {
        headers: getAuthHeader()
      });
      
      toast.success("Password changed successfully");
      setShowPasswordModal(false);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleConnectWithBank = async (applicationId, bankName) => {
    setConnectingBank(`${applicationId}-${bankName}`);
    try {
      await axios.post(
        `${API}/user/connect-bank`,
        { application_id: applicationId, bank_name: bankName },
        { headers: getAuthHeader() }
      );
      toast.success(`Request sent! A loan officer from ${bankName} will contact you within 48 hours.`);
      // Refresh applications to show updated status
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send request. Please try again.");
    } finally {
      setConnectingBank(null);
    }
  };

  const handleRequestFinancialPlanner = async (applicationId) => {
    if (selectedServices.length === 0) {
      toast.error("Please select at least one service");
      return;
    }
    
    setRequestingPlanner(true);
    try {
      await axios.post(
        `${API}/request-financial-planner`,
        { 
          application_id: applicationId, 
          services_requested: selectedServices 
        },
        { headers: getAuthHeader() }
      );
      toast.success("Request submitted! A financial planner will contact you within 24 hours.");
      setShowFinancialPlannerModal(null);
      setSelectedServices([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send request. Please try again.");
    } finally {
      setRequestingPlanner(false);
    }
  };

  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(s => s !== serviceId)
        : [...prev, serviceId]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#52BFB8]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 py-8">
      <div className="container-main">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="heading-2">My Applications</h1>
            <p className="text-gray-600">Welcome back, {user?.full_name}</p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/qualify">
              <Button className="btn-primary">
                New Application
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Button 
              variant="outline" 
              onClick={() => setShowPasswordModal(true)}
              data-testid="change-password-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Applications List */}
        {applications.length === 0 ? (
          <div className="card-base text-center py-16" data-testid="no-applications">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#4A4A4A] mb-2">No Applications Yet</h3>
            <p className="text-gray-600 mb-6">Start your loan pre-qualification journey today.</p>
            <Link to="/qualify">
              <Button className="btn-primary">
                Check My Eligibility
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => {
              const statusConfig = STATUS_CONFIG[app.application_status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConfig.icon;
              const isQualified = app.status === "qualified";
              const bestBank = app.matched_banks?.[0];

              return (
                <div 
                  key={app.id} 
                  className="card-base hover:shadow-lg transition-shadow"
                  data-testid={`application-${app.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Left: Status & Basic Info */}
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        isQualified ? "bg-[#52BFB8]/10" : "bg-gray-100"
                      }`}>
                        {isQualified ? (
                          <CheckCircle2 className="w-6 h-6 text-[#52BFB8]" />
                        ) : (
                          <XCircle className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            isQualified ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                          }`}>
                            {isQualified ? "Pre-Qualified" : "Not Qualified"}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </span>
                          {app.is_pro && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-[#52BFB8] to-[#3DA8A1] text-white flex items-center gap-1">
                              <Crown className="w-3 h-3" />
                              Pro
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          Applied on {formatDate(app.created_at)} • {app.employment_type === "government" ? "Government" : "Private"} Worker
                        </p>
                      </div>
                    </div>

                    {/* Middle: Loan Details */}
                    <div className="flex items-center gap-6 lg:gap-8">
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Requested</p>
                        <p className="font-semibold text-[#4A4A4A]">{formatCurrency(app.requested_amount)}</p>
                      </div>
                      
                      {isQualified && bestBank && (
                        <>
                          <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Approved</p>
                            <p className="font-semibold text-[#52BFB8]">{formatCurrency(bestBank.max_approved_amount)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Monthly</p>
                            <p className="font-semibold text-[#4A4A4A]">{formatCurrency(bestBank.estimated_monthly_payment)}</p>
                          </div>
                          <div className="text-center hidden sm:block">
                            <p className="text-xs text-gray-500 mb-1">Banks</p>
                            <p className="font-semibold text-[#4A4A4A]">{app.matched_banks?.length || 0}</p>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Right: Action */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {isQualified && app.is_pro && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setShowFinancialPlannerModal(app.id);
                            setSelectedServices([]);
                          }}
                          className="text-purple-600 border-purple-300 hover:bg-purple-50"
                          data-testid={`financial-planner-${app.id}`}
                        >
                          <Users className="w-4 h-4 mr-2" />
                          Financial Planner
                        </Button>
                      )}
                      {isQualified && app.is_pro && app.matched_banks?.length > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
                          className="text-[#52BFB8] border-[#52BFB8] hover:bg-[#52BFB8]/5"
                          data-testid={`connect-bank-toggle-${app.id}`}
                        >
                          <Building2 className="w-4 h-4 mr-2" />
                          Connect with Bank
                        </Button>
                      )}
                      {isQualified && !app.is_pro && (
                        <Button 
                          size="sm"
                          onClick={() => {
                            sessionStorage.setItem("qualificationResult", JSON.stringify(app));
                            navigate("/free-results");
                          }}
                          className="bg-gradient-to-r from-[#52BFB8] to-[#3DA8A1] text-white"
                          data-testid={`upgrade-to-pro-${app.id}`}
                        >
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade to Pro
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          sessionStorage.setItem("qualificationResult", JSON.stringify(app));
                          navigate(app.is_pro ? "/results" : "/free-results");
                        }}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Bank Options */}
                  {expandedApp === app.id && isQualified && app.matched_banks?.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-100">
                      <div className="mb-4">
                        <h4 className="font-semibold text-[#4A4A4A] mb-1">Ready to start your loan application?</h4>
                        <p className="text-sm text-gray-500">Select a bank below and we'll connect you with a loan officer.</p>
                      </div>
                      
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {app.matched_banks.map((bank, idx) => {
                          const isConnecting = connectingBank === `${app.id}-${bank.bank_name}`;
                          const isAlreadyConnected = app.connected_bank === bank.bank_name;
                          
                          return (
                            <div 
                              key={idx}
                              className={`p-4 rounded-xl border-2 transition-all ${
                                isAlreadyConnected 
                                  ? "border-[#52BFB8] bg-[#52BFB8]/5" 
                                  : "border-gray-200 hover:border-[#52BFB8]/50"
                              }`}
                            >
                              <div className="flex items-center gap-3 mb-3">
                                {bank.bank_logo ? (
                                  <img 
                                    src={bank.bank_logo} 
                                    alt={bank.bank_name}
                                    className="w-10 h-10 object-contain rounded"
                                  />
                                ) : (
                                  <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                                    <Building2 className="w-5 h-5 text-gray-400" />
                                  </div>
                                )}
                                <div>
                                  <p className="font-semibold text-[#4A4A4A] text-sm">{bank.bank_name}</p>
                                  <p className="text-xs text-gray-500">{bank.interest_rate}% APR</p>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                                <div>
                                  <p className="text-gray-500">Amount</p>
                                  <p className="font-semibold text-[#4A4A4A]">{formatCurrency(bank.max_approved_amount)}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Monthly</p>
                                  <p className="font-semibold text-[#4A4A4A]">{formatCurrency(bank.estimated_monthly_payment)}</p>
                                </div>
                              </div>
                              
                              {isAlreadyConnected ? (
                                <div className="flex items-center gap-2 text-sm text-[#52BFB8] font-medium">
                                  <CheckCircle2 className="w-4 h-4" />
                                  Request Sent
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  className="w-full bg-[#52BFB8] hover:bg-[#3DA8A1] text-white"
                                  onClick={() => handleConnectWithBank(app.id, bank.bank_name)}
                                  disabled={isConnecting || app.connected_bank}
                                  data-testid={`connect-${bank.bank_name.replace(/\s+/g, '-').toLowerCase()}`}
                                >
                                  {isConnecting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Send className="w-4 h-4 mr-2" />
                                      Connect Me
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {app.connected_bank && (
                        <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-200">
                          <div className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-green-800">Connection Request Sent to {app.connected_bank}</p>
                              <p className="text-sm text-green-700 mt-1">
                                We're connecting you with a loan officer. You'll receive a confirmation email shortly, 
                                and a representative will reach out within 48 hours.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Stats Summary */}
        {applications.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="card-base text-center">
              <p className="text-2xl font-bold text-[#4A4A4A]">{applications.length}</p>
              <p className="text-sm text-gray-500">Total Applications</p>
            </div>
            <div className="card-base text-center">
              <p className="text-2xl font-bold text-[#52BFB8]">
                {applications.filter(a => a.status === "qualified").length}
              </p>
              <p className="text-sm text-gray-500">Pre-Qualified</p>
            </div>
            <div className="card-base text-center">
              <p className="text-2xl font-bold text-[#3DA8A1]">
                {applications.filter(a => a.application_status === "in_progress").length}
              </p>
              <p className="text-sm text-gray-500">In Progress</p>
            </div>
            <div className="card-base text-center">
              <p className="text-2xl font-bold text-green-600">
                {applications.filter(a => a.application_status === "approved").length}
              </p>
              <p className="text-sm text-gray-500">Approved</p>
            </div>
          </div>
        )}

        {/* Document Upload Section */}
        <div className="mt-8">
          <div 
            className="card-base cursor-pointer"
            onClick={handleToggleDocuments}
            data-testid="documents-section-toggle"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#52BFB8]/10 rounded-lg flex items-center justify-center">
                  <Upload className="w-5 h-5 text-[#52BFB8]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#4A4A4A]">Upload Verification Documents</h3>
                  <p className="text-sm text-gray-500">ID, Job Letter, Pay Slips, Bank Statements</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {documents.length > 0 && (
                  <span className="text-sm text-gray-500">{documents.length} uploaded</span>
                )}
                {showDocuments ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>
          </div>
          
          {showDocuments && (
            <div className="card-base mt-2" data-testid="documents-upload-panel">
              {loadingDocs ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#52BFB8]" />
                </div>
              ) : (
                <DocumentUpload
                  authHeader={getAuthHeader()}
                  existingDocuments={documents}
                  onDocumentsChange={(newDocs) => setDocuments(newDocs)}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Financial Planner Modal */}
      {showFinancialPlannerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Connect with Financial Planner</h3>
                    <p className="text-sm text-gray-500">Select the services you need help with</p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setShowFinancialPlannerModal(null);
                    setSelectedServices([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3 mb-6">
                {financialPlannerServices.map((service) => {
                  const Icon = service.icon;
                  const isSelected = selectedServices.includes(service.id);
                  return (
                    <button
                      key={service.id}
                      onClick={() => toggleService(service.id)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        isSelected 
                          ? "border-purple-500 bg-purple-50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          isSelected ? "bg-purple-100" : "bg-gray-100"
                        }`}>
                          <Icon className={`w-4 h-4 ${isSelected ? "text-purple-600" : "text-gray-500"}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">{service.title}</p>
                            {isSelected && (
                              <CheckCircle2 className="w-4 h-4 text-purple-600" />
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{service.desc}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="bg-purple-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-purple-800">
                  <strong>What happens next?</strong> A qualified financial representative will contact you within 24 hours to discuss your selected services and create a personalized plan.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowFinancialPlannerModal(null);
                    setSelectedServices([]);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => handleRequestFinancialPlanner(showFinancialPlannerModal)}
                  disabled={selectedServices.length === 0 || requestingPlanner}
                >
                  {requestingPlanner ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Request Consultation
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#52BFB8]/10 rounded-xl flex items-center justify-center">
                    <Lock className="w-5 h-5 text-[#52BFB8]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Change Password</h3>
                    <p className="text-sm text-gray-500">Update your account password</p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label htmlFor="current-password">Current Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="current-password"
                      type="password"
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                      className="pl-10"
                      placeholder="Enter current password"
                      data-testid="current-password-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="new-password"
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                      className="pl-10"
                      placeholder="Min 6 characters"
                      data-testid="new-password-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="confirm-new-password"
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                      className="pl-10"
                      placeholder="Confirm new password"
                      data-testid="confirm-new-password-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#52BFB8] hover:bg-[#3DA8A1] text-white"
                    disabled={changingPassword}
                    data-testid="submit-password-change"
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Change Password"
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
